import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Input } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourseModulesTableColumnNames, fetchAllCourseModules, createCourseModules, updateCourseModules, 
    deleteCourseModules, fetchUsers, updateUserPreference,
    activateDeactivateCoursesModule,
    fetchTranslation,
    fetchAllTopics} from "../../../slices/thunks";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import CommonTable from "../../../Components/Common/Table/CommonTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import AddModal from "../../../Components/Common/Modal/AddModal";
import AddCourseModuleForm from "../../../Components/Common/AddCourseForm/AddCourseModuleForm";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import usePermissions from "../../../Components/Hooks/usePermissions"; 
import { toast } from "react-toastify";


const labels = [
  {
    id: 1,
    labelName: "ModuleNameEnglish",
    fieldName: "text-box-translate",
    name: "ModuleNameEn",
    value: "ModuleNameEn",
    required: true,
    translateTo: "ModuleNameAr",
    errorMessage: "Module Name English Is Require",
    minLength:3,
    maxLength: 70,
  },
  {
    id: 2,
    labelName: "ModuleNameArabic",
    fieldName: "text-box-translate",
    name: "ModuleNameAr",
    value: "ModuleNameAr",
    required: true,
    errorMessage:"Module Name Arabic Is Require",
    minLength:3,
    maxLength: 70,
  },
  {
    id: 3,
    labelName: "ModuleDescriptionEn",
    fieldName: "textarea-translate",
    name: "ModuleDescriptionEn",
    value: "ModuleDescriptionEn",
    required: true,
    translateTo: "ModuleDescriptionAr",
    errorMessage:"Module Description English Is Require",
    minLength:3,
    maxLength: 70,
  },
  {
    id: 4,
    labelName: "ModuleDescriptionAr",
    fieldName: "textarea-translate",
    name: "ModuleDescriptionAr",
    value: "ModuleDescriptionAr",
    required: true,
    errorMessage:"Module Description Arabic Is Require",
    minLength:3,
    maxLength: 70,
  },
  {
    id: 5,
    labelName: "Tutor",
    fieldName: "tutor-dropdown",
    name: "TutorID",
    value: "TutorID",
    required: true,
    errorMessage:"Tutor is Require"
  },
  {
    id: 6,
    labelName: "Topic",
    fieldName: "topic-dropdown",
    name: "TopicID",
    value: "TopicID",
    required: true,
    errorMessage:"Topic is Require"
  },
  {
    id: 7,
    labelName: "StartDate",
    fieldName: "date-picker",
    name: "StartDate",
    value: "StartDate",
    required: true,
    errorMessage:"Start Date is Require",
    disabled: (mode) => mode === 'isEdit', 
  },
  {
    id: 8,
    labelName: "Duration",
    fieldName: "number-box",
    name: "Duration",
    value: "Duration",
    required: true,
    errorMessage:"Duration is Require"
  },
]

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};


function CourseModules () {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const {user} = getAuthUser();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd"); 
  const [selectedRow, setSelectedRow] = useState({}); 
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [lastTranslatedField, setLastTranslatedField] = useState(null);
  

  const { Course, CourseModules, tableColumnNames, loading, loadings, module, translationLoading } = useSelector((state) => ({
    Course: state.CourseModules.course,  
    CourseModules: state.CourseModules.modules, 
    tableColumnNames: state.CourseModules.tableColumnNames,
    loading: state.CourseModules.loading,
    loadings: state.CourseModules.loadings,
    module: state.Module.module,
    translationLoading: state.Module.translationLoading, 
  }));

   // Compute permission
  const permission = (() => {
    // moduleId: 1 (Administration), pageId: 7 (Course module)
    return usePermissions(8, 7);
  })();

  const handleButtonClick = (module) => {
    navigate(`/manage-courses/${module.CourseID}/${module.ID}/sessions`)
  }

  const onAddClickHandler = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (module, value) => {
    setSelectedRow(module);
    setIsAddOrEdit(value);
    setShow(true);
  };
  
  const onTranslateClickHandler = (targetField, textToTranslate, formikSetFieldValue, sourceFieldName) => {
    if (!textToTranslate || textToTranslate.trim() === "") {
      toast.error(t("EnterTextToTranslate"));
      return;
    }
    setLastTranslatedField(sourceFieldName); 

    dispatch(fetchTranslation({ LangText: textToTranslate }))
      .unwrap() 
      .then(translatedText => {
        formikSetFieldValue(targetField, translatedText);
      })
      .catch(error => {
        console.error("Translation error:", error);
        toast.error(t("TranslationFailed"));
      })
      .finally(() => {
        setLastTranslatedField(null); 
      });
  };


  const onActiveOrDeactiveChange = (course, e) => {
    dispatch(activateDeactivateCoursesModule({ ID: course.ID, IsActive: course.IsActive}));
  };
  
  
  const onSubmitHandler = async (values, { resetForm }) => {
    try {
      const moduleObj = {
        CourseID: courseId,
        ModuleNameEn: values.ModuleNameEn,
        ModuleNameAr: values.ModuleNameAr,
        ModuleDescriptionEn: values.ModuleDescriptionEn,
        ModuleDescriptionAr: values.ModuleDescriptionAr,
        TutorID: values.TutorID,
        TopicID: values.TopicID,
        Duration: values.Duration,
        StartDate: values.StartDate,
      };

      if (isAddOrEdit === "isEdit" && selectedRow?.ID) {
        moduleObj.ID = selectedRow?.ID;
      }

      setShow(false);
      if (isAddOrEdit === "isAdd") {
        await dispatch(createCourseModules(moduleObj))
      } else {
        await dispatch(updateCourseModules(moduleObj))
      }
      
      resetForm();
    } catch (error) {
      console.error("Error submitting module:", error);
      toast.error(t("SubmissionFailed") + (error.message ? `: ${error.message}` : ''));
    }
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ColumnId] = selectedColumns.includes(column.ColumnId);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchCourseModulesTableColumnNames({
          module: "COURSEMODULES"
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteCourseModules({ Ids: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };
  
  useEffect(() => {
    dispatch(fetchCourseModulesTableColumnNames({ module: "COURSEMODULES" }))
    dispatch(fetchAllCourseModules({ CourseID: courseId }));
    dispatch(fetchUsers({ IsSystemUser: false }))
    dispatch(fetchAllTopics({IsActive:true}));

  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={i18n.language === 'en' ? Course?.CourseNameEn : Course?.CourseNameAr} back={true} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => onAddClickHandler("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                {loading || loadings ? (
                  <div className="d-flex justify-content-center align-items-center">
                  <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                    Loading...
                  </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                    {/* <CommonTable
                      data={CourseModules}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      handleButtonClick={handleButtonClick}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      hasAction={true}
                      permission={permission}
                    /> */}
                    <CommonDataTable
                      data={CourseModules}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      handleButtonClick={handleButtonClick}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow} 
        FormBody={AddCourseModuleForm}
        FormBodyProps={{ 
          onTranslateClickHandler, 
          translationLoading,
          lastTranslatedField: lastTranslatedField,
          currentModuleId: isAddOrEdit === "isEdit" ? selectedRow?.ID : null,
        }} 
        title={isAddOrEdit === "isAdd" ? t("AddNewModule") : t("EditModule")}
        size="lg"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={`You want to delete this course? This will also permanently remove all associated sessions.`}
      />
    </div>
  );
}

export default CourseModules;